// components/IEP/MaturityGauge.js
import React from 'react';
import { Box, Typography, LinearProgress, Chip, Tooltip } from '@mui/material';
import { TrendingUp, Psychology, Speed, Assessment } from '@mui/icons-material';

const MaturityGauge = ({ score, size = 'large', showLabel = true, showDetails = true }) => {
  const getMaturityLevel = (score) => {
    if (score >= 90) return { level: 'divine', color: '#8b5cf6', icon: '✨' };
    if (score >= 80) return { level: 'miraculous', color: '#6366f1', icon: '🌟' };
    if (score >= 70) return { level: 'unprecedented', color: '#3b82f6', icon: '🚀' };
    if (score >= 60) return { level: 'exceptional', color: '#10b981', icon: '🎯' };
    if (score >= 50) return { level: 'world_class', color: '#059669', icon: '🏆' };
    if (score >= 40) return { level: 'competitive', color: '#f59e0b', icon: '⚡' };
    if (score >= 30) return { level: 'developing', color: '#f97316', icon: '🌱' };
    return { level: 'foundational', color: '#ef4444', icon: '🛠️' };
  };

  const getRecommendation = (score) => {
    if (score >= 80) return "Focus on transcendent transformations and ecosystem leadership";
    if (score >= 60) return "Accelerate strategic initiatives and innovation capabilities";
    if (score >= 40) return "Build foundational capabilities and optimize operations";
    return "Establish basic systems and processes for growth";
  };

  const maturity = getMaturityLevel(score);
  
  const getIconComponent = (icon) => {
    const iconMap = {
      '✨': <span style={{ fontSize: '1.5rem' }}>✨</span>,
      '🌟': <span style={{ fontSize: '1.5rem' }}>🌟</span>,
      '🚀': <span style={{ fontSize: '1.5rem' }}>🚀</span>,
      '🎯': <span style={{ fontSize: '1.5rem' }}>🎯</span>,
      '🏆': <span style={{ fontSize: '1.5rem' }}>🏆</span>,
      '⚡': <span style={{ fontSize: '1.5rem' }}>⚡</span>,
      '🌱': <span style={{ fontSize: '1.5rem' }}>🌱</span>,
      '🛠️': <span style={{ fontSize: '1.5rem' }}>🛠️</span>,
    };
    return iconMap[icon] || <TrendingUp />;
  };

  const getNextLevel = (score) => {
    if (score >= 90) return { target: 95, improvement: 5 };
    if (score >= 80) return { target: 90, improvement: 10 };
    if (score >= 70) return { target: 80, improvement: 10 };
    if (score >= 60) return { target: 70, improvement: 10 };
    if (score >= 50) return { target: 60, improvement: 10 };
    if (score >= 40) return { target: 50, improvement: 10 };
    if (score >= 30) return { target: 40, improvement: 10 };
    return { target: 30, improvement: 20 };
  };

  const nextLevel = getNextLevel(score);

  if (size === 'small') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1, mr: 2 }}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Maturity
            </Typography>
            <Typography variant="caption" fontWeight="600">
              {score}/100
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={score}
            sx={{ 
              height: 6, 
              borderRadius: 3,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': {
                bgcolor: maturity.color,
              }
            }}
          />
        </Box>
        <Tooltip title={maturity.level.charAt(0).toUpperCase() + maturity.level.slice(1)}>
          <Chip 
            label={maturity.icon}
            size="small"
            sx={{ 
              bgcolor: `${maturity.color}15`,
              color: maturity.color,
              border: `1px solid ${maturity.color}30`
            }}
          />
        </Tooltip>
      </Box>
    );
  }

  if (size === 'medium') {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight="600">
            Maturity Score
          </Typography>
          <Box display="flex" alignItems="center">
            <Typography variant="h6" fontWeight="700" mr={1}>
              {score}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              /100
            </Typography>
          </Box>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={score}
          sx={{ 
            height: 12, 
            borderRadius: 6,
            bgcolor: 'action.hover',
            mb: 1,
            '& .MuiLinearProgress-bar': {
              bgcolor: maturity.color,
              borderRadius: 6,
            }
          }}
        />
        
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Chip 
            label={maturity.level.charAt(0).toUpperCase() + maturity.level.slice(1)}
            size="small"
            sx={{ 
              bgcolor: `${maturity.color}15`,
              color: maturity.color,
              border: `1px solid ${maturity.color}30`,
              fontWeight: 600
            }}
            icon={getIconComponent(maturity.icon)}
          />
          <Typography variant="caption" color="text.secondary">
            Next: {nextLevel.target}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Large size (default)
  return (
    <Box>
      {/* Score Display */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          {showLabel && (
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Organizational Maturity
            </Typography>
          )}
          <Box display="flex" alignItems="baseline">
            <Typography 
              variant="h1" 
              fontWeight="800" 
              sx={{ 
                background: `linear-gradient(90deg, ${maturity.color}, ${maturity.color}aa)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {score}
            </Typography>
            <Typography variant="h4" color="text.secondary" ml={1}>
              /100
            </Typography>
          </Box>
        </Box>
        <Box 
          sx={{ 
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${maturity.color}15`,
            border: `3px solid ${maturity.color}30`,
            fontSize: '2rem'
          }}
        >
          {getIconComponent(maturity.icon)}
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Current Level
          </Typography>
          <Typography variant="body2" fontWeight="600">
            {maturity.level.charAt(0).toUpperCase() + maturity.level.slice(1)}
          </Typography>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={score}
          sx={{ 
            height: 16, 
            borderRadius: 8,
            bgcolor: 'action.hover',
            mb: 1,
            '& .MuiLinearProgress-bar': {
              bgcolor: maturity.color,
              borderRadius: 8,
              backgroundImage: `linear-gradient(90deg, ${maturity.color}, ${maturity.color}dd)`,
            }
          }}
        />
        
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Foundational
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Divine
          </Typography>
        </Box>
      </Box>

      {showDetails && (
        <>
          {/* Next Level Target */}
          <Box 
            sx={{ 
              p: 2, 
              borderRadius: 2,
              bgcolor: 'action.hover',
              mb: 2
            }}
          >
            <Typography variant="subtitle2" gutterBottom color="primary">
              <Psychology sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1rem' }} />
              Next Level Target
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2">
                  Target: <strong>{nextLevel.target}/100</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Need {nextLevel.improvement} points improvement
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                color={maturity.color}
                fontWeight="700"
              >
                +{nextLevel.improvement}
              </Typography>
            </Box>
          </Box>

          {/* AI Recommendation */}
          <Box 
            sx={{ 
              p: 2, 
              borderRadius: 2,
              bgcolor: 'primary.light',
              border: '1px solid',
              borderColor: 'primary.main',
              background: `linear-gradient(145deg, ${maturity.color}10, ${maturity.color}05)`
            }}
          >
            <Typography variant="subtitle2" gutterBottom color="primary">
              <Psychology sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1rem' }} />
              AI Recommendation
            </Typography>
            <Typography variant="body2">
              {getRecommendation(score)}
            </Typography>
          </Box>

          {/* Level Indicators */}
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Maturity Levels
            </Typography>
            <Box display="flex" justifyContent="space-between" mt={1}>
              {[
                { min: 0, max: 30, label: 'Foundational', color: '#ef4444' },
                { min: 30, max: 40, label: 'Developing', color: '#f97316' },
                { min: 40, max: 50, label: 'Competitive', color: '#f59e0b' },
                { min: 50, max: 60, label: 'World Class', color: '#059669' },
                { min: 60, max: 70, label: 'Exceptional', color: '#10b981' },
                { min: 70, max: 80, label: 'Unprecedented', color: '#3b82f6' },
                { min: 80, max: 90, label: 'Miraculous', color: '#6366f1' },
                { min: 90, max: 100, label: 'Divine', color: '#8b5cf6' },
              ].map((level) => (
                <Tooltip key={level.label} title={`${level.label}: ${level.min}-${level.max}`}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: score >= level.min && score < level.max ? level.color : `${level.color}30`,
                      border: `2px solid ${score >= level.min && score < level.max ? level.color : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.3)',
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MaturityGauge;